'use client';

import { createContext, useContext, useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * A slot element the shell publishes once it has mounted. The context holds
 * this STORE, whose identity never changes, and a portal reads the element
 * through it. A context value that changed on mount would reach every streamed
 * page boundary that has not hydrated yet, and React answers a context change
 * above a pending boundary by discarding the streamed HTML and client-rendering
 * the boundary; when the stream had already queued that boundary's reveal, the
 * streamed copy then stays behind in a hidden node, every test id twice (found
 * with the routes' loading boundaries, 2026-09-27).
 */
export interface SlotStore {
  /** The shell's ref callback: stable, so React calls it only on mount and unmount. */
  set: (el: HTMLElement | null) => void;
  get: () => HTMLElement | null;
  subscribe: (listener: () => void) => () => void;
  /** A page's portal holds the slot (`TopbarSlot` claims it on mount and
   *  releases it on unmount), so whatever the shell draws in the slot's place
   *  renders nothing rather than hiding (docs/LOADING.md §4.2). Counted: two
   *  pages' portals overlap for a frame during a handover. */
  claim: () => () => void;
  filled: () => boolean;
}

export function createSlotStore(): SlotStore {
  let el: HTMLElement | null = null;
  let claims = 0;
  const listeners = new Set<() => void>();
  const notify = () => {
    for (const listener of listeners) listener();
  };
  return {
    set: (next) => {
      if (next === el) return;
      el = next;
      notify();
    },
    get: () => el,
    claim: () => {
      claims += 1;
      notify();
      let released = false;
      return () => {
        if (released) return;
        released = true;
        claims -= 1;
        notify();
      };
    },
    filled: () => claims > 0,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

const noSubscribe = () => () => {};

/** The element a slot store holds; null on the server and while hydrating. */
function useSlotElement(store: SlotStore | null): HTMLElement | null {
  return useSyncExternalStore(
    store?.subscribe ?? noSubscribe,
    () => store?.get() ?? null,
    () => null,
  );
}

/**
 * Lets a page render content into the app shell's single top bar. The bar
 * carries the view AS AN OBJECT + global functions - title + count, its
 * presentation (the rail switch), its access (the audience face-pile), the
 * view's object controls, Search, the profile menu (see DESIGN_SYSTEM.md "The
 * global bar and the content head"); controls that shape the rows below
 * (filter, grouping/sort, create) live in the page's content head
 * (`src/components/content-head.tsx`), never here. The
 * shell publishes the bar's left slot element through this context;
 * `TopbarSlot` portals its children into it. The portal fills in just after
 * hydration, so the bar height is reserved by the shell to avoid any shift.
 */
export const TopbarSlotContext = createContext<SlotStore | null>(null);

export function TopbarSlot({ children }: { children: ReactNode }) {
  const store = useContext(TopbarSlotContext);
  const el = useSlotElement(store);
  useEffect(() => store?.claim(), [store]);
  return el ? createPortal(children, el) : null;
}

/** Whether a page's portal holds the slot (`SlotStore.claim`); false on the
 *  server and while hydrating, where the shell's CSS covers the frame. */
export function useSlotFilled(store: SlotStore): boolean {
  return useSyncExternalStore(store.subscribe, store.filled, () => false);
}

/**
 * The bar's **right** region, just left of the personal/account menu: a page that is
 * scoped to a group (a board) portals its members face-pile here, so "who has access
 * to this screen" sits next to "my account" rather than in the left title slot.
 */
export const TopbarRightSlotContext = createContext<SlotStore | null>(null);

export function TopbarRightSlot({ children }: { children: ReactNode }) {
  const el = useSlotElement(useContext(TopbarRightSlotContext));
  return el ? createPortal(children, el) : null;
}

/**
 * The peek-chrome seam (2026-07-20): the intercepted item peek seats INSIDE the
 * app chrome - the sidebar and the top bar stay mounted, visible, and
 * interactive; the peek covers the content area only, and the *fullest* screen
 * is the one where the person has collapsed the sidebar (it is no longer a
 * full-viewport opaque page). The shell publishes the top bar's **peek slot**
 * (where the peek's path trail renders, mirroring the direct-visit page, which
 * portals the same trail into the top bar) and a **setter** the peek toggles on
 * mount/unmount, so the shell can swap the top-bar's base header out for the
 * peek trail (`data-peek`) while a peek is open. `Modal` is the only consumer,
 * and the shell provides this context around the peek slot ONLY: its value
 * changes on mount and whenever a peek opens, and none of that may reach the
 * page's streamed boundaries (see `SlotStore`).
 */
export interface PeekChrome {
  /** The top bar's peek slot element; the peek portals its path trail here. */
  topbarEl: HTMLElement | null;
  /** Whether a peek is up right now. A peek that mounts while another is up
   *  (the loading boundary's handing over to the page's, one item's to the
   *  next on a drill) replaces it, and does not fade in again. */
  active: boolean;
  /** The peek calls this `true` on mount, `false` on unmount. The shell
   *  counts the calls, so a peek is up while any caller still holds it. */
  setActive: (active: boolean) => void;
}
export const PeekChromeContext = createContext<PeekChrome | null>(null);
